import React from 'react';
import { TrendingUp, CheckCircle, AlertCircle, ArrowRight, DollarSign, PiggyBank, Home } from 'lucide-react';

export const ResultsDisplay = ({ leadData }) => {
  const { 
    liquidity_grade, 
    liquidity_score, 
    interest_leak, 
    home_value, 
    mortgage_balance, 
    high_interest_debt,
    monthly_cash_surplus 
  } = leadData;

  const equity = home_value - mortgage_balance;
  const potentialSavings = interest_leak * 5;

  const getGradeColor = (grade) => {
    const colors = {
      'A': 'text-emerald-400',
      'B': 'text-gold-400',
      'C': 'text-amber-400',
      'D': 'text-orange-400',
      'F': 'text-red-400',
    };
    return colors[grade] || 'text-slate-400';
  };

  const getGradeBgColor = (grade) => {
    const colors = {
      'A': 'bg-emerald-400/10 border-emerald-400/30',
      'B': 'bg-gold-400/10 border-gold-400/30',
      'C': 'bg-amber-400/10 border-amber-400/30',
      'D': 'bg-orange-400/10 border-orange-400/30',
      'F': 'bg-red-400/10 border-red-400/30',
    };
    return colors[grade] || 'bg-slate-400/10';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const recommendations = [
    {
      icon: Home,
      title: 'Consolidate High-Interest Debt',
      description: `You have ${formatCurrency(equity)} in home equity. Consolidating ${formatCurrency(high_interest_debt)} of high-interest debt into your mortgage could reduce your effective interest rate from 22% to under 5%.`,
      savings: `Potential Annual Savings: ${formatCurrency(interest_leak * 0.77)}`,
    },
    {
      icon: PiggyBank,
      title: 'Optimize Monthly Cash Flow',
      description: `With ${formatCurrency(monthly_cash_surplus)} monthly surplus, strategic restructuring could accelerate your debt-free timeline while building wealth.`,
      savings: null,
    },
    {
      icon: DollarSign,
      title: 'Stop the 5-Year Interest Leak',
      description: `Without action, you're projected to lose ${formatCurrency(potentialSavings)} over the next 5 years to high-interest payments.`,
      savings: null,
    },
  ];

  // Chart data for 5-year projection
  const chartData = [
    { year: 'Now', current: 0, optimized: 0 },
    { year: 'Year 1', current: interest_leak, optimized: interest_leak * 0.23 },
    { year: 'Year 2', current: interest_leak * 2, optimized: interest_leak * 0.46 },
    { year: 'Year 3', current: interest_leak * 3, optimized: interest_leak * 0.69 },
    { year: 'Year 4', current: interest_leak * 4, optimized: interest_leak * 0.92 },
    { year: 'Year 5', current: interest_leak * 5, optimized: interest_leak * 1.15 },
  ];

  const maxChartValue = Math.max(...chartData.map(d => d.current));

  return (
    <div data-testid="results-display" className="space-y-10 fade-in">
      {/* Grade Display */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className={`glass-card rounded-2xl p-8 border ${getGradeBgColor(liquidity_grade)}`}>
          <h3 className="text-sm font-heading uppercase tracking-widest text-slate-400 mb-4">
            Your Liquidity Grade
          </h3>
          <div className="flex items-center gap-6">
            <div 
              className={`text-7xl md:text-8xl font-heading font-bold ${getGradeColor(liquidity_grade)}`}
              data-testid="liquidity-grade"
            >
              {liquidity_grade}
            </div>
            <div>
              <div className="text-3xl font-semibold text-slate-50" data-testid="liquidity-score">
                {liquidity_score}/100
              </div>
              <div className="text-sm text-slate-400">Optimization Score</div>
            </div>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-8">
          <h3 className="text-sm font-heading uppercase tracking-widest text-slate-400 mb-4">
            Current Annual Interest Leak
          </h3>
          <div className="text-4xl md:text-5xl font-heading font-bold text-red-400" data-testid="annual-leak">
            {formatCurrency(interest_leak)}
          </div>
          <div className="mt-4 flex items-center gap-2 text-emerald-400">
            <TrendingUp className="w-5 h-5" />
            <span className="font-medium">
              Potential to reduce by {formatCurrency(interest_leak * 0.77)}/year
            </span>
          </div>
        </div>
      </div>

      {/* 5-Year Projection Chart */}
      <div className="glass-card rounded-2xl p-8">
        <h3 className="text-xl font-heading font-semibold text-slate-50 mb-2">
          5-Year Wealth Optimization Projection
        </h3>
        <p className="text-slate-400 text-sm mb-8">
          Comparison of cumulative interest costs: Current path vs. Optimized restructure
        </p>

        <div className="relative h-64 md:h-80">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-xs text-slate-500 w-16">
            <span>{formatCurrency(maxChartValue)}</span>
            <span>{formatCurrency(maxChartValue * 0.5)}</span>
            <span>$0</span>
          </div>

          {/* Chart area */}
          <div className="ml-20 h-full pb-8 flex items-end justify-between gap-3 md:gap-6">
            {chartData.map((item, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex gap-1 justify-center items-end h-48 md:h-60">
                  {/* Current path bar */}
                  <div 
                    className="w-1/2 bg-red-400/60 rounded-t transition-all duration-500"
                    style={{ 
                      height: `${(item.current / maxChartValue) * 100}%`,
                      animationDelay: `${index * 100}ms`
                    }}
                  />
                  {/* Optimized path bar */}
                  <div 
                    className="w-1/2 bg-emerald-400/60 rounded-t transition-all duration-500"
                    style={{ 
                      height: `${(item.optimized / maxChartValue) * 100}%`,
                      animationDelay: `${index * 100}ms`
                    }}
                  />
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap">{item.year}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-8 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-400/60 rounded" />
            <span className="text-sm text-slate-400">Current Path</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-400/60 rounded" />
            <span className="text-sm text-slate-400">Optimized</span>
          </div>
        </div>

        {/* Savings callout */}
        <div className="mt-6 p-4 bg-emerald-400/10 border border-emerald-400/30 rounded-lg text-center">
          <span className="text-emerald-400 font-semibold">
            Potential 5-Year Savings: {formatCurrency(potentialSavings - (interest_leak * 1.15))}
          </span>
        </div>
      </div>

      {/* Recommendations */}
      <div>
        <h3 className="text-xl font-heading font-semibold text-slate-50 mb-6">
          How to Close Your Interest Leak
        </h3>
        <div className="space-y-4">
          {recommendations.map((rec, index) => (
            <div 
              key={index}
              className="glass-card rounded-xl p-6 flex gap-4 fade-in"
              style={{ animationDelay: `${(index + 1) * 150}ms` }}
            >
              <div className="flex-shrink-0 p-3 bg-gold-400/10 rounded-lg h-fit">
                <rec.icon className="w-6 h-6 text-gold-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-slate-50 mb-2 flex items-center gap-2">
                  {rec.title}
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                </h4>
                <p className="text-slate-400 text-sm">
                  {rec.description}
                </p>
                {rec.savings && (
                  <p className="text-emerald-400 font-medium text-sm mt-2">
                    {rec.savings}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps CTA */}
      <div className="glass-card rounded-2xl p-8 text-center gold-glow">
        <AlertCircle className="w-12 h-12 text-gold-400 mx-auto mb-4" />
        <h3 className="text-xl md:text-2xl font-heading font-semibold text-slate-50 mb-3">
          Ready to Stop the Leak?
        </h3>
        <p className="text-slate-400 max-w-xl mx-auto mb-6">
          Schedule a complimentary 10-minute review with Sanam to discuss your personalized restructuring options.
        </p>
        <a
          href="https://calendly.com/sanam-everythingmortgages/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex items-center gap-2"
          data-testid="cta-schedule-btn"
        >
          Schedule My Audit Review
          <ArrowRight className="w-5 h-5" />
        </a>
      </div>
    </div>
  );
};
